import './CountryCard.css';
import { useId } from 'preact/hooks';
import { CandidateRow } from '../../components/CandidateRow';
import { Chip } from '../../components/Chip';
import { SectionLabel } from '../../components/SectionLabel';
import { UfTile } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { fmtSections, fmtVoters } from '../../format/numbers';
import { placeName } from '../../format/places';
import { cityRows, countryRow } from '../../model/abroad';
import { openProfile } from '../../state/actions';
import {
  useConfig,
  useHeadline,
  useLayout,
  useNowMinute,
  usePhase,
  useRouteState,
  useStatics,
  useTimeline,
  useView,
} from '../../state/hooks';
import { CityRow } from './CityRow';
import { PlaceCard } from './PlaceCard';
import { PlaceHeader } from './PlaceHeader';
import { UfNavButtons } from './UfNavButtons';
import { UpLink } from './UpLink';
import { useStateDetail } from './useStateDetail';

/**
 * Cartão do país do exterior (§8.5 CountryCard): "‹ Exterior", o ladrilho com o ISO, o presidente no país (a soma das
 * cidades, `model/abroad.ts`) e as cidades em ordem de eleitorado ("Onde se vota" quando há uma só). O resultado por
 * cidade vem do detalhe de `ZZ`; fora do ao vivo, a nota de que os votos por cidade só existem para agora.
 * Neutralidade: linhas na ordem 13 | 22.
 */
export function CountryCard() {
  const route = useRouteState();
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const layout = useLayout();
  const phase = usePhase();
  const headline = useHeadline();
  const { live, displayTMin } = useTimeline();
  // o instante do fechamento das urnas: ao vivo, o relógio da fonte (antes do 1º número a linha do tempo não anda);
  // revendo a noite, o instante exibido (§8.5 "Dados")
  const nowTMin = useNowMinute();
  const instant = live ? nowTMin : displayTMin;
  const id = useId();
  const seq = view?.seq ?? null;
  const detail = useStateDetail('ZZ', live, seq);
  const iso2 = route.place.kind === 'country' ? route.place.iso2 : null;
  const geo = statics.geography;
  const country = geo?.countries.find((c) => c.iso2 === iso2) ?? null;
  if (iso2 === null) return null;

  const tv = layout.mode === 'tv';
  const pair = view?.candidates ?? statics.candidates?.president ?? null;
  const photos = config.features.photos;
  const beforeOpen = phase === 'pre_open' || headline === 'countdown';
  const cities = geo
    ? cityRows(
        geo.abroad_cities.filter((c) => c.country_iso2 === iso2),
        detail?.value,
        view?.abroad,
        pair,
        instant,
      ).sort((a, b) => b.city.electorate_ref - a.city.electorate_ref)
    : [];
  const row = country ? countryRow(country, cities) : null;
  const votes = !beforeOpen && row?.votes && row.votes[0] + row.votes[1] > 0 ? row.votes : null;
  const note = beforeOpen
    ? t('common.votesFrom17')
    : !live
      ? t('country.replay')
      : votes
        ? null
        : detail === null || detail.status === 'loading'
          ? t('country.loading')
          : detail.status === 'error' && detail.value === null
            ? t('country.error')
            : t('uf.noSections');
  const name = country?.name ?? iso2;

  return (
    <PlaceCard kind="country" class="country-card" state={votes ? 'votes' : 'no_votes'}>
      <UpLink to={placeName('ZZ')} label={t('place.up.aria.abroad')} />
      <PlaceHeader
        title={name}
        sub={
          country
            ? t('place.sub', { S: fmtSections(row?.sectionsFraction ?? 0), E: fmtVoters(country.electorate_ref) })
            : null
        }
        seal={<UfTile code={iso2} fill="plain" size="big" />}
        buttons={tv ? 0 : 1}
      />
      <UfNavButtons kind="country" />
      <section class="place-card__section" data-testid="country-card-president" aria-labelledby={`${id}-president`}>
        <SectionLabel
          id={`${id}-president`}
          label={t('tabs.president')}
          actions={row && row.sectionsFraction >= 1 ? <Chip tone="done" label={t('chip.counted')} /> : undefined}
        />
        {note ? (
          <p class="place-note" data-testid="country-card-note">
            {note}
          </p>
        ) : null}
        {votes && pair && row?.shares ? (
          <div class="country-card__rows">
            {pair.map((c, i) => (
              <CandidateRow
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                share={row.shares?.[i as 0 | 1] ?? null}
                portraitSize={30}
                onOpen={tv ? undefined : () => openProfile(c.number, 'president', 'list')}
              />
            ))}
          </div>
        ) : null}
      </section>
      {tv ? null : (
        <section class="place-card__section" aria-labelledby={`${id}-cities`}>
          <SectionLabel id={`${id}-cities`} label={t(cities.length === 1 ? 'country.whereVote' : 'country.cities')} />
          <div class="country-card__list" data-testid="country-card-cities">
            {cities.map((c) => (
              <CityRow key={c.city.tse} row={c} pair={pair} photos={photos} />
            ))}
          </div>
        </section>
      )}
    </PlaceCard>
  );
}
